import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ActionStopTransactionIcon } from '../../assets/icons/action-stop-transaction.svg';
import { ReactComponent as FacilityIcon } from '../../assets/icons/sidebar/sidebar-facility.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableChargingSession,
  ChargingSessionRequest,
  RequestSessionInterFace,
  StopChargingRequest,
} from '../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchChargingSessions,
  setChargingSessionFilterData,
  updateChargingFilters,
  fetchListFilters,
  setSortData,
} from '../../redux/slice/ChargingSessionSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import { setSelectedStation } from '../../redux/slice/StationManagementSidePanelSlice';
import { RootState } from '../../redux/store';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import DeviceViewSidePanel from '../../shared-components/table-side-panel/DeviceViewSidePanel';
import FacilityViewSidePanel from '../../shared-components/table-side-panel/FacilityViewSidePanel';
import StationViewSidePanel from '../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import chargingSessionApi from '../../utils/api/charging-session';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Function to list current and upcoming charging transactions
 * @returns
 */
function ChargingSession() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { t } = useTranslation('chargingSession');
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const {
    chargingSessions,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    sortField,
    sortBy,
    isFilterUpdated,
  } = useAppSelector((state: RootState) => state.chargingSession);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);

  /**
   * Handle the fetch data for Api call function
   * @param data - ChargingSessionRequest
   */
  const fetchChargingSession = (data: ChargingSessionRequest) => {
    dispatch(fetchChargingSessions(data));
  };

  /**
   * Handle the fetch data for Api call function
   * @param data - RequestSessionInterFace
   */
  const fetchData = (data: RequestSessionInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchChargingSessions({
          ...data,
          // facilityId: selectedFacility?._id,
        }),
      );
    }
  };

  /**
   * Handles the pagination
   *
   */
  const handlePageChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchChargingSession({
      ...queryParameters,
      page: changePage,
      limit,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchChargingSession({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the modal close function
   */
  const handleCloseModal = () => {
    setModalBoxStatus('close');
  };

  /**
   * Handle the Stop Charging Transaction function
   */
  const handleStopCharging = (requestData: StopChargingRequest) => {
    chargingSessionApi
      .stopCharging(requestData)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('stopRequestSent')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCloseModal}
            />
          ),
          notificationModal: true,
        });
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({ ...queryParameters, page, limit, sortBy, sortField });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the Stop Charging Transaction function
   */
  const handleStopChargingWarning = (chargingSession: AvailableChargingSession) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={chargingSession.id}
          title={t('stopWarningTitle')}
          description={t('stopWarningDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          // submitSvgIcon={<ActionStopTransactionIcon />}
          submitButtonOnClick={(id) =>
            id &&
            handleStopCharging({
              chargingPointSn: chargingSession.chargingPointSn,
              transactionId: chargingSession.transactionId,
            })
          }
          submitButtonLabel={t('stopCharging')}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingSessionDate: AvailableChargingSession, newTab: boolean) => {
    if (chargingSessionDate) {
      dispatch(
        setSelectedStation({
          id: chargingSessionDate?.chargingStationId,
          name: chargingSessionDate?.chargingStation,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/transaction-management/station/${chargingSessionDate.chargingStationId}/station-dashboard?searchBy=${chargingSessionDate.chargingStation}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/transaction-management/station/${chargingSessionDate.chargingStationId}/station-dashboard`,
          search: `?searchBy=${chargingSessionDate.chargingStation}`,
        });
      }
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Renders the custom data for the actions column
   * @param {AvailableChargingSession} chargingSession - The charging session data
   * @returns {JSX.Element} The rendered actions cell
   */
  const renderActions = (chargingSession: AvailableChargingSession) => (
    <div className="action-button-group">
      {chargingSession.status === 'inProgress' && (
        <div
          className="action-button-item view-color"
          onClick={() => handleStopChargingWarning(chargingSession as AvailableChargingSession)}
          role="presentation">
          <ActionStopTransactionIcon />
        </div>
      )}
    </div>
  );

  const column = [
    // {
    //   label: 'Date',
    //   accessorKey: 'date' as keyof AvailableChargingSession,
    //   sortAllow: false,
    // },
    // {
    //   label: 'Time',
    //   accessorKey: 'time' as keyof AvailableChargingSession,
    //   sortAllow: false,
    // },
    {
      label: t('chargingStartedAt'),
      accessorKey: 'transactionStartsAt' as keyof AvailableChargingSession,
      sortAllow: true,
    },
    {
      label: t('chargingEndedAt'),
      accessorKey: 'transactionEndsAt' as keyof AvailableChargingSession,
      sortAllow: true,
    },
    {
      label: t('facilityName'),
      accessorKey: 'facilityTagName' as keyof AvailableChargingSession,
      sortAllow: false,
      queryParamsKey: 'facilityId',
      queryParamsValue: 'facilityTagId',
      sidePanelComponent: <FacilityViewSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof AvailableChargingSession,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof AvailableChargingSession,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceViewSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('userName'),
      accessorKey: 'endConsumer' as keyof AvailableChargingSession,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof AvailableChargingSession,
    },
    {
      label: t('actions'),
      customData: renderActions,
      accessorKey: '' as keyof AvailableChargingSession,
      columnWidth: 150,
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setChargingSessionFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchChargingSession({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated || chargingSessions.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        limit: DefaultApiLimit.limit,
        page: DefaultApiLimit.page,
      });
    }
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('facilityId') || searchParameters.get('add');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('sessions'),
          icon: <FacilityIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('sessions'),
          icon: <FacilityIcon />,
          count: undefined,
        }),
      );
    }
  }, [searchParameters, totalResults]);

  return (
    <div className="charging-session-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={chargingSessions}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateChargingFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          sortData={{
            sortBy,
            sortField,
          }}
        />
      </div>
    </div>
  );
}

export default ChargingSession;
